import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import type {
    CreateCaseEventPayload,
    CreateClaimItemPayload,
    CreateClaimPayload,
    CreateLegalCasePayload,
    CreateLegalDeadlinePayload,
    CreateLegalMeasurePayload,
    CreateCourtDecisionPayload,
    CreateHearingPayload,
    CreatePartyPayload,
    CreateSeizedAssetPayload,
    CreateLegalAgreementPayload,
    CreateJudicialDepositPayload,
    CreateLegalCaseDocumentPayload,
    CreateBeneficiaryPayload,
    CreateExpensePayload,
    CloseProvisionPeriodPayload,
    LegalAgreementStatus,
    LegalDeadlineType,
    LegalMeasureStatus,
    SeizedAssetStatus,
    SupersedeLegalAgreementPayload,
    UpdateCourtDecisionPayload,
    UpdateHearingPayload,
    UpdateLegalAgreementPayload,
    UpdateJudicialDepositPayload,
    UpdateBeneficiaryPayload,
    UpdateExpensePayload,
    UpdateSeizedAssetPayload,
} from '../types/LegalCase';

const API = appConfig.apis.recoveryApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string, public data?: any) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        // 204 No Content (idiomático em DELETE) ou corpo vazio: não tentar res.json(),
        // que lançaria SyntaxError e quebraria uma operação que deu certo.
        if (res.status === 204) return null;
        const text = await res.text();
        return text ? JSON.parse(text) : null;
    }
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.message || `API error: ${res.status}`, data);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

// GET que devolve texto cru (ex.: CSV do export por caso). Não faz JSON.parse — o corpo é o
// próprio arquivo. Em erro, tenta ler a mensagem JSON como as demais chamadas.
const getText = async (path: string): Promise<string> => {
    const res = await fetch(`${baseUrl}${path}`, { headers: await getHeaders() });
    if (res.ok) return res.text();
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.message || `API error: ${res.status}`, data);
};

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify(data),
    }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, {
        method: 'PUT',
        headers: await getHeaders(),
        body: JSON.stringify(data),
    }));

const del = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, {
        method: 'DELETE',
        headers: await getHeaders(),
    }));

// Convenção de query param do recovery: page/size/sort (não pageSize/sortField).
const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const recoveryApi = {
    getLegalCases: (params?: {
        page?: number; size?: number; sort?: string;
        search?: string; pole?: string; type?: string; status?: string;
        measureType?: string; measurePending?: boolean; deadlineWithinDays?: number;
        responsibleFirmId?: string; cpc25Classification?: string; creditorEntity?: string;
    }) => get(`/legal-cases/page-list${qs(params || {})}`),

    getLegalCaseView: (uuid: string) => get(`/legal-cases/${uuid}/page-view`),

    getLegalCaseFormOptions: () => get(`/legal-cases/page-form`),

    // Relatório consolidado CPC 25 — Task 12 (equivalente calculado da aba VALORES).
    getCpc25Report: (params?: { creditorEntity?: string }) =>
        get(`/legal-cases/cpc25-report${qs(params || {})}`),

    // Export por caso do CPC 25 — Task 20. CSV (texto), uma linha por caso, para a auditoria
    // conferir *quais* casos compõem cada número (o relatório é agregado). Filtro opcional.
    getCpc25CaseExportCsv: (params?: { creditorEntity?: string }): Promise<string> =>
        getText(`/legal-cases/cpc25-report/export${qs(params || {})}`),

    // Fechamentos contábeis congelados do CPC 25 — Task 20.
    getProvisionSnapshots: () => get(`/legal-cases/provision-snapshots`),

    getProvisionSnapshot: (snapshotUuid: string) =>
        get(`/legal-cases/provision-snapshots/${snapshotUuid}`),

    // Fecha o período numa data-base. Restrito ao jurídico (ADMIN/LEGAL) no backend (403).
    // Data-base futura é recusada (422): não se fecha período que ainda não terminou.
    closeProvisionPeriod: (data: CloseProvisionPeriodPayload) =>
        post(`/legal-cases/provision-snapshots`, data as unknown as Record<string, unknown>),

    // Anula um fechamento (justificativa obrigatória). Não apaga nem reabre — marca o registro
    // preservando os números e a trilha. Restrito ao jurídico (403); 422 sem justificativa ou se
    // já estiver anulado.
    voidProvisionSnapshot: (snapshotUuid: string, reason: string) =>
        put(`/legal-cases/provision-snapshots/${snapshotUuid}/void`, { reason }),

    createLegalCase: (data: CreateLegalCasePayload) => post(`/legal-cases`, data as unknown as Record<string, unknown>),

    updateLegalCase: (uuid: string, data: CreateLegalCasePayload) =>
        put(`/legal-cases/${uuid}`, data as unknown as Record<string, unknown>),

    // Partes e papéis — Task 3 (rotas aninhadas no caso).
    getParties: (caseUuid: string) => get(`/legal-cases/${caseUuid}/parties`),

    addParty: (caseUuid: string, data: CreatePartyPayload) =>
        post(`/legal-cases/${caseUuid}/parties`, data as unknown as Record<string, unknown>),

    removeParty: (caseUuid: string, partyUuid: string) =>
        del(`/legal-cases/${caseUuid}/parties/${partyUuid}`),

    // Pretensão e itens — Task 4 (rotas aninhadas no caso).
    getClaim: (caseUuid: string) => get(`/legal-cases/${caseUuid}/claim`),

    createClaim: (caseUuid: string, data: CreateClaimPayload) =>
        post(`/legal-cases/${caseUuid}/claim`, data as unknown as Record<string, unknown>),

    updateClaim: (caseUuid: string, data: CreateClaimPayload) =>
        put(`/legal-cases/${caseUuid}/claim`, data as unknown as Record<string, unknown>),

    addClaimItem: (caseUuid: string, data: CreateClaimItemPayload) =>
        post(`/legal-cases/${caseUuid}/claim/items`, data as unknown as Record<string, unknown>),

    // Edição de título — AF-3691. PUT parcial: só os campos enviados mudam.
    updateClaimItem: (caseUuid: string, itemUuid: string, data: CreateClaimItemPayload) =>
        put(
            `/legal-cases/${caseUuid}/claim/items/${itemUuid}`,
            data as unknown as Record<string, unknown>,
        ),

    removeClaimItem: (caseUuid: string, itemUuid: string) =>
        del(`/legal-cases/${caseUuid}/claim/items/${itemUuid}`),

    // Andamento processual — Task 5 (rotas aninhadas no caso).
    getCaseEvents: (caseUuid: string) => get(`/legal-cases/${caseUuid}/events`),

    addCaseEvent: (caseUuid: string, data: CreateCaseEventPayload) =>
        post(`/legal-cases/${caseUuid}/events`, data as unknown as Record<string, unknown>),

    removeCaseEvent: (caseUuid: string, eventUuid: string) =>
        del(`/legal-cases/${caseUuid}/events/${eventUuid}`),

    // Medidas judiciais — Task 6 (rotas aninhadas no caso).
    getMeasures: (caseUuid: string) => get(`/legal-cases/${caseUuid}/measures`),

    addMeasure: (caseUuid: string, data: CreateLegalMeasurePayload) =>
        post(`/legal-cases/${caseUuid}/measures`, data as unknown as Record<string, unknown>),

    updateMeasureStatus: (caseUuid: string, measureUuid: string, status: LegalMeasureStatus) =>
        put(`/legal-cases/${caseUuid}/measures/${measureUuid}/status`, { status }),

    removeMeasure: (caseUuid: string, measureUuid: string) =>
        del(`/legal-cases/${caseUuid}/measures/${measureUuid}`),

    // Prazos processuais — Task 6 (rotas aninhadas no caso).
    getDeadlines: (caseUuid: string) => get(`/legal-cases/${caseUuid}/deadlines`),

    addDeadline: (caseUuid: string, data: CreateLegalDeadlinePayload) =>
        post(`/legal-cases/${caseUuid}/deadlines`, data as unknown as Record<string, unknown>),

    updateDeadline: (
        caseUuid: string,
        deadlineUuid: string,
        data: { deadlineType?: LegalDeadlineType; dueDate?: string; description?: string; fulfilled?: boolean },
    ) => put(`/legal-cases/${caseUuid}/deadlines/${deadlineUuid}`, data as unknown as Record<string, unknown>),

    removeDeadline: (caseUuid: string, deadlineUuid: string) =>
        del(`/legal-cases/${caseUuid}/deadlines/${deadlineUuid}`),

    // Bens penhorados — Task 7 (rotas aninhadas no caso).
    getSeizedAssets: (caseUuid: string) => get(`/legal-cases/${caseUuid}/seized-assets`),

    addSeizedAsset: (caseUuid: string, data: CreateSeizedAssetPayload) =>
        post(`/legal-cases/${caseUuid}/seized-assets`, data as unknown as Record<string, unknown>),

    updateSeizedAsset: (caseUuid: string, assetUuid: string, data: UpdateSeizedAssetPayload) =>
        put(`/legal-cases/${caseUuid}/seized-assets/${assetUuid}`, data as unknown as Record<string, unknown>),

    updateSeizedAssetStatus: (caseUuid: string, assetUuid: string, status: SeizedAssetStatus) =>
        put(`/legal-cases/${caseUuid}/seized-assets/${assetUuid}/status`, { status }),

    removeSeizedAsset: (caseUuid: string, assetUuid: string) =>
        del(`/legal-cases/${caseUuid}/seized-assets/${assetUuid}`),

    // Decisões judiciais — Task 8 (rotas aninhadas no caso).
    getCourtDecisions: (caseUuid: string) => get(`/legal-cases/${caseUuid}/court-decisions`),

    addCourtDecision: (caseUuid: string, data: CreateCourtDecisionPayload) =>
        post(`/legal-cases/${caseUuid}/court-decisions`, data as unknown as Record<string, unknown>),

    updateCourtDecision: (caseUuid: string, decisionUuid: string, data: UpdateCourtDecisionPayload) =>
        put(`/legal-cases/${caseUuid}/court-decisions/${decisionUuid}`, data as unknown as Record<string, unknown>),

    removeCourtDecision: (caseUuid: string, decisionUuid: string) =>
        del(`/legal-cases/${caseUuid}/court-decisions/${decisionUuid}`),

    // Audiências — Task 8 (rotas aninhadas no caso).
    getHearings: (caseUuid: string) => get(`/legal-cases/${caseUuid}/hearings`),

    addHearing: (caseUuid: string, data: CreateHearingPayload) =>
        post(`/legal-cases/${caseUuid}/hearings`, data as unknown as Record<string, unknown>),

    updateHearing: (caseUuid: string, hearingUuid: string, data: UpdateHearingPayload) =>
        put(`/legal-cases/${caseUuid}/hearings/${hearingUuid}`, data as unknown as Record<string, unknown>),

    removeHearing: (caseUuid: string, hearingUuid: string) =>
        del(`/legal-cases/${caseUuid}/hearings/${hearingUuid}`),

    // Acordos com versionamento — Task 9 (rotas aninhadas no caso).
    getAgreements: (caseUuid: string) => get(`/legal-cases/${caseUuid}/agreements`),

    addAgreement: (caseUuid: string, data: CreateLegalAgreementPayload) =>
        post(`/legal-cases/${caseUuid}/agreements`, data as unknown as Record<string, unknown>),

    updateAgreement: (caseUuid: string, agreementUuid: string, data: UpdateLegalAgreementPayload) =>
        put(`/legal-cases/${caseUuid}/agreements/${agreementUuid}`, data as unknown as Record<string, unknown>),

    updateAgreementStatus: (caseUuid: string, agreementUuid: string, status: LegalAgreementStatus) =>
        put(`/legal-cases/${caseUuid}/agreements/${agreementUuid}/status`, { status }),

    // Renegociação: cria o sucessor com estes dados e marca o atual como substituído.
    supersedeAgreement: (caseUuid: string, agreementUuid: string, data: SupersedeLegalAgreementPayload) =>
        post(
            `/legal-cases/${caseUuid}/agreements/${agreementUuid}/supersede`,
            data as unknown as Record<string, unknown>,
        ),

    removeAgreement: (caseUuid: string, agreementUuid: string) =>
        del(`/legal-cases/${caseUuid}/agreements/${agreementUuid}`),

    // Depósitos judiciais — Task 10 (rotas aninhadas no caso). O levantamento é registrado
    // via update (withdrawnAmount + withdrawnAt), não tem rota própria.
    getDeposits: (caseUuid: string) => get(`/legal-cases/${caseUuid}/deposits`),

    addDeposit: (caseUuid: string, data: CreateJudicialDepositPayload) =>
        post(`/legal-cases/${caseUuid}/deposits`, data as unknown as Record<string, unknown>),

    updateDeposit: (caseUuid: string, depositUuid: string, data: UpdateJudicialDepositPayload) =>
        put(`/legal-cases/${caseUuid}/deposits/${depositUuid}`, data as unknown as Record<string, unknown>),

    removeDeposit: (caseUuid: string, depositUuid: string) =>
        del(`/legal-cases/${caseUuid}/deposits/${depositUuid}`),

    // Anexos do caso — Task 13 (rotas aninhadas). O arquivo sobe direto ao barn (presigned
    // PUT via FileManager); estas rotas só gerenciam o vínculo barnFileId→caso. O download
    // sai do barn (getDownloadUrl), não daqui.
    getDocuments: (caseUuid: string) => get(`/legal-cases/${caseUuid}/documents`),

    addDocument: (caseUuid: string, data: CreateLegalCaseDocumentPayload) =>
        post(`/legal-cases/${caseUuid}/documents`, data as unknown as Record<string, unknown>),

    removeDocument: (caseUuid: string, documentUuid: string) =>
        del(`/legal-cases/${caseUuid}/documents/${documentUuid}`),

    // Beneficiários — Task 11 (registro global, rotas top-level).
    getBeneficiaries: (params?: { search?: string; type?: string }) =>
        get(`/beneficiaries/page-list${qs(params || {})}`),

    createBeneficiary: (data: CreateBeneficiaryPayload) =>
        post(`/beneficiaries`, data as unknown as Record<string, unknown>),

    updateBeneficiary: (beneficiaryUuid: string, data: UpdateBeneficiaryPayload) =>
        put(`/beneficiaries/${beneficiaryUuid}`, data as unknown as Record<string, unknown>),

    removeBeneficiary: (beneficiaryUuid: string) =>
        del(`/beneficiaries/${beneficiaryUuid}`),

    // Despesas — Task 11 (raiz própria, rotas top-level; caso/beneficiário opcionais).
    getExpenses: (params?: {
        search?: string; category?: string; legalCaseId?: string; beneficiaryId?: string;
        hasCase?: boolean;
    }) => get(`/expenses/page-list${qs(params || {})}`),

    createExpense: (data: CreateExpensePayload) =>
        post(`/expenses`, data as unknown as Record<string, unknown>),

    updateExpense: (expenseUuid: string, data: UpdateExpensePayload) =>
        put(`/expenses/${expenseUuid}`, data as unknown as Record<string, unknown>),

    removeExpense: (expenseUuid: string) =>
        del(`/expenses/${expenseUuid}`),
};
